import React, { useState, useRef } from "react";
import "./CareerForm.css";

const CareerForm = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    role: "",
    message: "",
    resume: null,
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // Refs for scrolling to errors
  const fieldRefs = {
    fullName: useRef(null),
    phone: useRef(null),
    email: useRef(null),
    role: useRef(null),
    resume: useRef(null),
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "resume") {
      const file = files[0];
      if (file) {
        const allowedTypes = [
          "application/pdf",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ];
        if (!allowedTypes.includes(file.type)) {
          setErrors((prev) => ({ ...prev, resume: "Only PDF or DOCX files allowed" }));
          return;
        }
        if (file.size > 500 * 1024) {
          setErrors((prev) => ({ ...prev, resume: "File size must be less than 500KB" }));
          return;
        }
        setErrors((prev) => ({ ...prev, resume: "" }));
      }
      setFormData({ ...formData, resume: file });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const validate = () => {
    let newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = "Full Name is required";
    if (!formData.phone.trim() || !/^\d{10}$/.test(formData.phone))
      newErrors.phone = "Valid 10-digit phone number required";
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Valid email required";
    if (!formData.role) newErrors.role = "Role is required";
    if (!formData.resume) newErrors.resume = "Resume is required (PDF/DOCX under 500KB)";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    setErrors(newErrors);

    // If there are errors, scroll to the first one
    if (Object.keys(newErrors).length > 0) {
      const firstErrorKey = Object.keys(newErrors)[0];
      if (fieldRefs[firstErrorKey] && fieldRefs[firstErrorKey].current) {
        fieldRefs[firstErrorKey].current.scrollIntoView({ behavior: "smooth", block: "center" });
        fieldRefs[firstErrorKey].current.focus();
      }
      return;
    }

    setSubmitting(true);

    const form = new FormData();
    form.append("fullName", formData.fullName);
    form.append("phone", formData.phone);
    form.append("email", formData.email);
    form.append("role", formData.role);
    form.append("message", formData.message);
    form.append("resume", formData.resume);

    try {
      const response = await fetch("https://newzealacademy.com/send-career-mail.php", {
        method: "POST",
        body: form,
      });

      if (response.ok) {
        alert("🎉 Congratulations! You have successfully submitted your application. We will get back to you shortly!");
        handleReset();
      } else {
        alert("Failed to send application. Please try again.");
      }
    } catch (err) {
      alert("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      fullName: "",
      phone: "",
      email: "",
      role: "",
      message: "",
      resume: null,
    });
    setErrors({});
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <form className="nz-career-form" onSubmit={handleSubmit}>
      <h2 className="nz-form-title">Submit Application</h2>

      <div className="nz-form-row">
        <div className="nz-form-group">
          <input
            ref={fieldRefs.fullName}
            type="text"
            name="fullName"
            placeholder="Full Name*"
            value={formData.fullName}
            onChange={handleChange}
          />
          {errors.fullName && <p className="errorr">{errors.fullName}</p>}
        </div>

        <div className="nz-form-group">
          <input
            ref={fieldRefs.phone}
            type="text"
            name="phone"
            placeholder="Phone Number*"
            value={formData.phone}
            onChange={handleChange}
          />
          {errors.phone && <p className="errorr">{errors.phone}</p>}
        </div>
      </div>

      <div className="nz-form-row">
        <div className="nz-form-group">
          <input
            ref={fieldRefs.email}
            type="email"
            name="email"
            placeholder="Email*"
            value={formData.email}
            onChange={handleChange}
            required
          />
          {errors.email && <p className="errorr">{errors.email}</p>}
        </div>

        <div className="nz-form-group">
          <select ref={fieldRefs.role} name="role" value={formData.role} onChange={handleChange}>
            <option value="">Role*</option>
            <option value="English Teachers">English Teachers</option>
            <option value="Coordinator">Coordinator</option>
            <option value="Sales & Marketing">Sales & Marketing Executive</option>
          </select>
          {errors.role && <p className="errorr">{errors.role}</p>}
        </div>
      </div>

      <div className="nz-form-row">
        <div className="nz-form-group">
          <label>Upload Resume*</label>
          <input
            ref={fieldRefs.resume}
            type="file"
            name="resume"
            accept=".pdf,.docx"
            onChange={handleChange}
            ref={fileInputRef}
          />
          {errors.resume && <p className="errorr">{errors.resume}</p>}
        </div>
      </div>

      <p className="nz-form-subtitle">PDF or DOCX Format and size should be less than 500kb</p>

      <div className="nz-form-group nz-full-width">
        <textarea
          name="message"
          placeholder="Type your message"
          value={formData.message}
          onChange={handleChange}
        ></textarea>
      </div>

      <div className="nz-submit-wrapper">
        <button type="button" className="nz-submit-btn" onClick={handleReset}>
          Reset
        </button>
        <button type="submit" className="nz-submit-btn" disabled={submitting}>
          {submitting ? "Submitting..." : "Submit Application"}
        </button>
      </div>
    </form>
  );
};

export default CareerForm;
